import React from 'react';
import { Eye, Download, RefreshCw, CreditCard, Smartphone, Wallet } from 'lucide-react';
import TransactionStatusBadge from './TransactionStatusBadge';

const TransactionTable = ({
  transactions, loading,
  onViewDetails, onDownloadReceipt, onRefund,
  currentPage, totalPages, onPageChange
}) => {

  const formatDate = (s) => new Date(s).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  const paymentIcon = (method) => {
    switch (method?.toLowerCase()) {
      case 'card': case 'credit_card': case 'debit_card': return <CreditCard className="w-3.5 h-3.5" />;
      case 'upi': case 'mobile': return <Smartphone className="w-3.5 h-3.5" />;
      case 'wallet': return <Wallet className="w-3.5 h-3.5" />;
      default: return <CreditCard className="w-3.5 h-3.5" />;
    }
  };

  const canRefund = (t) =>
    t.status === 'completed' && new Date(t.date) > new Date(Date.now() - 30 * 86400000);

  /* ── Loading skeleton ── */
  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="animate-pulse space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex gap-4">
            {[...Array(6)].map((_, j) => <div key={j} className="h-3 bg-gray-100 rounded flex-1" />)}
          </div>
        ))}
      </div>
    </div>
  );

  /* ── Empty state ── */
  if (!transactions?.length) return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
      <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-200" />
      <h3 className="text-base font-semibold text-gray-700 mb-1">No transactions found</h3>
      <p className="text-sm text-gray-400">Try adjusting your filters or search term.</p>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        {/*
          min-w-[720px]:  fits ~800px viewport minus sidebar
          Payment Method column: hidden at md (768–1023px), visible at lg (1024px+)
          All text is xs for density; rows have comfortable py-3
        */}
        <table className="w-full min-w-[720px] lg:min-w-full">
          <thead className="bg-teal-700 text-white">
            <tr>
              <th className="px-3 md:px-4 lg:px-5 py-3 text-left text-white font-semibold text-white uppercase tracking-wider w-[150px]">Transaction</th>
              <th className="px-3 md:px-4 lg:px-5 py-3 text-left text-white font-semibold text-white uppercase tracking-wider w-[160px]">Customer</th>
              <th className="px-3 md:px-4 lg:px-5 py-3 text-left text-white font-semibold text-white uppercase tracking-wider w-[110px]">Amount</th>
              {/* Hidden md, visible lg */}
              <th className="hidden lg:table-cell px-3 md:px-4 lg:px-5 py-3 text-left text-white font-semibold text-white uppercase tracking-wider w-[140px]">Payment Method </th>
              <th className="px-3 md:px-4 lg:px-5 py-3 text-left text-white font-semibold text-white uppercase tracking-wider w-[100px]">Status</th>
              <th className="px-3 md:px-4 lg:px-5 py-3 text-left text-white font-semibold text-white uppercase tracking-wider w-[140px]">Date</th>
              <th className="px-3 md:px-4 lg:px-5 py-3 text-right text-white font-semibold text-white uppercase tracking-wider w-[80px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {transactions.map((t) => (
              <tr key={t.id} className="hover:bg-gray-50/70 transition-colors group">

                {/* Transaction ID */}
                <td className="px-3 md:px-4 lg:px-5 py-3">
                  <p className="text-xs font-semibold text-gray-800 truncate max-w-[130px]" title={`${t.transactionId}`}>
                    #{t.transactionId}
                  </p>
                  {t.orderId && (
                    <p className="text-[11px] text-gray-400 truncate max-w-[130px]">Order #{t.orderId}</p>
                  )}
                </td>

                {/* Customer */}
                <td className="px-3 md:px-4 lg:px-5 py-3">
                  <p className="text-xs font-medium text-gray-800 truncate max-w-[150px]">{t.customer?.name || '—'}</p>
                  <p className="text-[11px] text-gray-400 truncate max-w-[150px]">{t.customer?.email}</p>
                </td>

                {/* Amount */}
                <td className="px-3 md:px-4 lg:px-5 py-3">
                  <p className="text-xs font-semibold text-gray-900">₹{t.amount.toFixed(2)}</p>
                  {t.fees && <p className="text-[11px] text-gray-400">Fees ₹{t.fees.toFixed(2)}</p>}
                </td>

                {/* Payment Method (lg+) */}
                <td className="hidden lg:table-cell px-3 md:px-4 lg:px-5 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">{paymentIcon(t.paymentMethod)}</span>
                    <div>
                      <p className="text-xs text-gray-700 capitalize">{t.paymentMethod?.replace('_',' ') || '—'}</p>
                      {t.paymentDetails && <p className="text-[11px] text-gray-400 truncate max-w-[110px]">{t.paymentDetails}</p>}
                    </div>
                  </div>
                </td>

                {/* Status */}
                <td className="px-3 md:px-4 lg:px-5 py-3">
                  <TransactionStatusBadge status={t.status} />
                </td>

                {/* Date */}
                <td className="px-3 md:px-4 lg:px-5 py-3">
                  <p className="text-xs text-gray-600 whitespace-nowrap">{formatDate(t.date)}</p>
                </td>

                {/* Actions */}
                <td className="px-3 md:px-4 lg:px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onViewDetails(t)} className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors" title="View Details">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    {t.status === 'completed' && (
                      <button onClick={() => onDownloadReceipt(t)} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors" title="Download Receipt">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {canRefund(t) && (
                      <button onClick={() => onRefund(t)} className="p-1.5 rounded-lg text-orange-600 hover:bg-gray-50 transition-colors" title="Process Refund">
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-gray-50 px-4 md:px-5 py-3 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">Page <span className="font-semibold text-gray-700">{currentPage}</span> of {totalPages}</p>
          <div className="flex gap-1.5">
            <PagBtn onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>‹ Prev</PagBtn>
            {[...Array(Math.min(5, totalPages))].map((_, i) => (
              <PagBtn key={i+1} onClick={() => onPageChange(i+1)} active={currentPage === i+1}>{i+1}</PagBtn>
            ))}
            <PagBtn onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next ›</PagBtn>
          </div>
        </div>
      )}
    </div>
  );
};

function PagBtn({ onClick, disabled, active, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
        active
          ? 'bg-teal-600 text-white border-teal-600 font-semibold'
          : disabled
            ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-500'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

export default TransactionTable;